// frontend/src/pages/AnalyticsDashboard.js
import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL;

function StatCard({ label, value }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 16,
        padding: 18,
      }}
    >
      <div style={{ opacity: 0.7, marginBottom: 8 }}>{label}</div>

      <div style={{ fontSize: 34, fontWeight: 900 }}>{value}</div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [key, setKey] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  async function loadDashboard() {
    if (!key.trim()) {
      setError("Missing analytics key");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const response = await fetch(`${API_URL}/analytics/dashboard`, {
        headers: {
          "x-analytics-key": key.trim(),
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Load dashboard failed");
      }

      setData(result);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const savedKey = localStorage.getItem("elacoria_analytics_key") || "";

    if (savedKey) {
      setKey(savedKey);
    }
  }, []);

  function handleSaveKey() {
    localStorage.setItem("elacoria_analytics_key", key.trim());
    loadDashboard();
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#080808",
        color: "#fff",
        padding: 32,
        fontFamily: "Arial, sans-serif",
      }}
    >
      <h1>Elacoria Analytics</h1>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 28,
          maxWidth: 720,
        }}
      >
        <input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Analytics admin key"
          type="password"
          style={{
            flex: 1,
            padding: 14,
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.15)",
            background: "rgba(255,255,255,0.06)",
            color: "#fff",
          }}
        />

        <button
          onClick={handleSaveKey}
          style={{
            padding: "14px 20px",
            borderRadius: 12,
            border: 0,
            cursor: "pointer",
            fontWeight: 800,
          }}
        >
          Load
        </button>
      </div>

      {isLoading && <p>Loading...</p>}

      {error && <p style={{ color: "#ff7777" }}>{error}</p>}

      {data && (
        <>
          <h2>Today: {data.today.day}</h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 36,
            }}
          >
            <StatCard label="Page Views" value={data.today.totalPageViews} />
            <StatCard label="Events" value={data.today.totalEvents} />
            <StatCard label="Trailer Plays" value={data.today.trailerPlays} />
            <StatCard label="Steam Clicks" value={data.today.steamClicks} />
            <StatCard label="Social Clicks" value={data.today.socialClicks} />
            <StatCard label="Support Clicks" value={data.today.supportClicks} />
            <StatCard label="PayPal Starts" value={data.today.paypalStarts} />
            <StatCard label="PayPal Success" value={data.today.paypalSuccess} />
            <StatCard label="Discord Starts" value={data.today.discordStarts} />
            <StatCard
              label="Discord Claims"
              value={data.today.discordClaimSuccess}
            />
          </div>

          <h2>Conversion</h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 36,
            }}
          >
            <StatCard
              label="Trailer Play Rate"
              value={`${data.conversion.trailerPlayRate}%`}
            />

            <StatCard
              label="Steam CTR"
              value={`${data.conversion.steamClickRate}%`}
            />

            <StatCard
              label="Support CTR"
              value={`${data.conversion.supportClickRate}%`}
            />

            <StatCard
              label="PayPal Start Rate"
              value={`${data.conversion.paypalStartRate}%`}
            />

            <StatCard
              label="PayPal Success Rate"
              value={`${data.conversion.paypalSuccessRate}%`}
            />

            <StatCard
              label="Discord Claim Rate"
              value={`${data.conversion.discordClaimRate}%`}
            />
          </div>

          <h2>Top Countries</h2>

          <table
            style={{
              width: "100%",
              marginBottom: 36,
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th align="left">Country</th>
                <th align="left">Continent</th>
                <th align="right">Visitors</th>
              </tr>
            </thead>

            <tbody>
              {(data.countries || []).map((country) => (
                <tr key={country.countryCode}>
                  <td>
                    {country.countryCode} - {country.country}
                  </td>
                  <td>{country.continent || "-"}</td>
                  <td align="right">{country.visitors}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Top Pages</h2>

          <table
            style={{
              width: "100%",
              marginBottom: 36,
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th align="left">Page</th>
                <th align="right">Views</th>
              </tr>
            </thead>

            <tbody>
              {(data.topPages || []).map((page) => (
                <tr key={page.page}>
                  <td>{page.page}</td>
                  <td align="right">{page.totalPageViews}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Recent Visitors</h2>

          <table
            style={{
              width: "100%",
              marginBottom: 36,
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th align="left">Country</th>
                <th align="left">City</th>
                <th align="left">Page</th>
                <th align="right">Views</th>
                <th align="left">Returning</th>
                <th align="left">Last Visit</th>
              </tr>
            </thead>

            <tbody>
              {(data.recentVisitors || []).map((visitor) => (
                <tr key={visitor.visitorId}>
                  <td>{visitor.countryCode || visitor.country || "-"}</td>
                  <td>{visitor.city || "-"}</td>
                  <td>{visitor.latestPage}</td>
                  <td align="right">{visitor.totalPageViews}</td>
                  <td>{visitor.isReturningVisitor ? "Yes" : "No"}</td>
                  <td>{visitor.lastVisitAt}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Recent Events</h2>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th align="left">Event</th>
                <th align="left">Page</th>
                <th align="left">Country</th>
                <th align="left">City</th>
                <th align="left">Time</th>
              </tr>
            </thead>

            <tbody>
              {(data.recentEvents || []).map((event) => (
                <tr key={event.eventId}>
                  <td>{event.eventType}</td>
                  <td>{event.page}</td>
                  <td>{event.countryCode || event.country || "-"}</td>
                  <td>{event.city || "-"}</td>
                  <td>{event.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
